import { CandidateDeclarationSource } from "constants/candidateDeclarations";
import { get } from "lodash";

function createFormikValueGetter(fieldPath: string) {
  return formikContextValues => get(formikContextValues, fieldPath);
}

function getCandidateDeclarationField(values, path) {
  return get(values, `candidateDeclaration.${path}`);
}

function createCandidateDeclarationGetter(field: string) {
  return formikValues => getCandidateDeclarationField(formikValues, field);
}

export const commonValueMap = {
  NVD: createCandidateDeclarationGetter("serialNumber"),

  vendor_property_id: createCandidateDeclarationGetter("originPropertyId"),

  id: createFormikValueGetter("suggestedConsignmentId"),

  "declaration.VERSION": createCandidateDeclarationGetter("nvdVersion"),
};

function yesNoTransformer(value) {
  if (value === "Yes") {
    return true;
  }
  if (value === "No") {
    return false;
  }
  return null;
}

const MIN_FIELD_CONFIDENCE: number = 0.7;

function getOcrAnswer(fieldName: string, transformer = yesNoTransformer) {
  return formikValues => {
    const value = getCandidateDeclarationField(
      formikValues,
      `declarationContent.${fieldName}`,
    );

    if (!value || value.confidence < MIN_FIELD_CONFIDENCE) {
      return undefined;
    }
    return transformer(value.text);
  };
}

function getSheepOwnedSinceBirth(value) {
  return (
    {
      "Less than 2 months": "A",
      "2-6 months": "B",
      "6-12 months": "C",
      "More than 12 months": "D",
    }[value] || null
  );
}

export const scannedNvdSuggestedValueMap = {
  ...commonValueMap,
  "declaration.qa_accredited": getOcrAnswer("qa_program"),
  "declaration.treated_for_scabby_mouth": getOcrAnswer("scabby_mouth"),
  "declaration.treated_with_hgp": getOcrAnswer("hgp_treated"),
  "declaration.fed_feed_containing_animal_fats": getOcrAnswer("animal_fats"),
  "declaration.owned_since_birth": getOcrAnswer("owned_since_birth"),
  "declaration.owned_since_birth_duration": getOcrAnswer(
    "ownership_duration",
    getSheepOwnedSinceBirth,
  ),
  "declaration.fed_byproduct_stockfeeds": getOcrAnswer("byproduct_stockfeeds"),
  "declaration.chemical_residue_restrictions": getOcrAnswer("erp_restrictions"),
  "declaration.veterinary_treatment": getOcrAnswer("whp_esi"),
  "declaration.consumed_material_in_withholding_period": getOcrAnswer(
    "consumed_whp_material",
  ),
  "declaration.spray_drift_risk": getOcrAnswer("spray_risk"),
};

export function getCandidateDeclarationAnswers(
  values,
): { questionId: string; value: string }[] {
  return (
    getCandidateDeclarationField(values, "declarationContent.answers") || []
  );
}

function getEnvdAnswer(questionId: number, transformer = yesNoTransformer) {
  return formikValues => {
    const answer = getCandidateDeclarationAnswers(formikValues).find(
      a =>
        // Question Ids are stored as strings, compare coercively
        a.questionId === String(questionId),
    );

    if (!answer) {
      return "";
    }

    return transformer(answer.value);
  };
}

function getOwnedSinceBirthDuration(formikValues) {
  const answers = getCandidateDeclarationAnswers(formikValues);
  const sheepAnswer = answers.find(a => a.questionId === String(19));
  if (sheepAnswer) {
    return (
      {
        "Less than 2 months": "A",
        "2-6 months": "B",
        "6-12 months": "C",
        "more than 12 months": "D",
      }[sheepAnswer.value] || null
    );
  }
  const cattleAnswer = answers.find(a => a.questionId === String(20));
  if (cattleAnswer) {
    return (
      {
        LT2Months: "A",
        "2to6Months": "B",
        "6to12Months": "C",
        GT12Months: "D",
      }[cattleAnswer.value] || null
    );
  }
  return null;
}

export const eNvdCandidateDecMap = {
  ...commonValueMap,

  quantity_NVD: createCandidateDeclarationGetter("declarationContent.heads"),

  // Sheep Q1
  "declaration.qa_accredited": getEnvdAnswer(30),

  // Cattle Q1
  "declaration.treated_with_hgp": getEnvdAnswer(88),

  // Sheep Q2
  "declaration.treated_for_scabby_mouth": getEnvdAnswer(96), // ERR this is the same as vet treatment - we need to double check this!!!

  // Sheep Q3 / Cattle Q3
  "declaration.owned_since_birth": getEnvdAnswer(17),

  "declaration.owned_since_birth_duration": getOwnedSinceBirthDuration,

  // Sheep Q4 / Cattle Q6
  "declaration.veterinary_treatment": getEnvdAnswer(96), // ERR this is the same as scabby mouth - we need to double check this!!!

  // Cattle 04
  "declaration.fed_byproduct_stockfeeds": getEnvdAnswer(59),

  // Sheep Q5 / Cattle Q7
  "declaration.consumed_material_in_withholding_period": getEnvdAnswer(70),

  // Cattle Q5
  "declaration.chemical_residue_restrictions": getEnvdAnswer(56),

  // Sheep Q6 / Cattle Q2
  "declaration.fed_feed_containing_animal_fats": getEnvdAnswer(58),

  // Cattle Q8
  "declaration.spray_drift_risk": getEnvdAnswer(102),
};

export const nvdSuggestedValueMap: Record<
  CandidateDeclarationSource,
  Record<string, (formikValues: unknown) => unknown>
> = {
  [CandidateDeclarationSource.ENVD]: eNvdCandidateDecMap,
  [CandidateDeclarationSource.CONSIGNMENT_ATTACHMENT_OCR]:
    scannedNvdSuggestedValueMap,
};
